import { DefaultUserRole } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import { FormFooter } from '@/components/basicComponents/formFooter';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { EditInvestorFormFieldsWrapper, EditInvestorModalPageWrapper } from '@/components/investors-page/editInvestorPage/styles';
import { UPDATE_DEFAULT_USER_ROLES_MUTATION } from '@/components/settings-page/queries';
import { TwoColumnWrapper } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { defaultUsersSchema, IFirmInfoFormData, userRoleMap } from '../types';

interface EditDefaultUsersModalPageProps {
  defaultUsersFormData: IFirmInfoFormData['defaultUsers'];
  usersSelectOptions: Record<keyof typeof userRoleMap, ISelectValue[]>;
  tenantId: string;
  listUsersRefetch: () => Promise<ApolloQueryResult<any>>;
  handleCloseModalPage: () => void;
}

export const EditDefaultUsersModalPage = ({
  defaultUsersFormData,
  usersSelectOptions,
  tenantId,
  listUsersRefetch,
  handleCloseModalPage
}: EditDefaultUsersModalPageProps) => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();
  const {
    control,
    formState: { isSubmitting },
    reset,
    handleSubmit
  } = useForm<IFirmInfoFormData['defaultUsers']>({
    resolver: zodResolver(defaultUsersSchema)
  });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [updateDefaultUserRoles] = useMutation(UPDATE_DEFAULT_USER_ROLES_MUTATION);

  useEffect(() => {
    if (!defaultUsersFormData) return;
    reset(defaultUsersFormData);
  }, [defaultUsersFormData]);

  const onSubmit = async (data: IFirmInfoFormData['defaultUsers']) => {
    try {
      setErrorMessage(null);

      const defaultUserRoles = [
        {
          defaultUserRole: DefaultUserRole.PrimaryAdmin,
          userId: data.primaryAdmin
        },
        {
          defaultUserRole: DefaultUserRole.AdvisorTeamDefault,
          userId: data.advisorTeamDefault
        },
        {
          defaultUserRole: DefaultUserRole.InvestmentTeamDefault,
          userId: data.investmentTeamDefault
        },
        {
          defaultUserRole: DefaultUserRole.OperationsTeamDefault,
          userId: data.operationsTeamDefault
        },
        {
          defaultUserRole: DefaultUserRole.ComplianceTeamDefault,
          userId: data.complianceTeamDefault
        }
      ];

      await updateDefaultUserRoles({
        variables: {
          data: {
            tenantId: tenantId || user?.tenant?.id,
            defaultUserRoles
          }
        }
      });

      await listUsersRefetch();

      handleCloseModalPage();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    }
  };

  return (
    <>
      {isSubmitting && (
        <ModalWrappedContent isOpen={isSubmitting} title="Saving" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleCloseModalPage} backToTitle="Firm Info" />} />
        <PageTitle title="Default Users" />
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <EditInvestorModalPageWrapper>
          <form onSubmit={handleSubmit(onSubmit)}>
            <EditDefaultUsersContentWrapper>
              <EditInvestorFormFieldsWrapper>
                <TwoColumnWrapper isMobile={isMobile}>
                  <Controller
                    name="primaryAdmin"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Select
                        label="Primary Admin"
                        value={value || ''}
                        setValue={onChange}
                        options={usersSelectOptions.primaryAdmin}
                        errorText={error?.message}
                        size="md"
                        isRequiredField
                      />
                    )}
                  />
                  <Controller
                    name="advisorTeamDefault"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Select
                        label="Advisor Team"
                        value={value || ''}
                        setValue={onChange}
                        options={usersSelectOptions.advisorTeamDefault}
                        errorText={error?.message}
                        size="md"
                        isRequiredField
                      />
                    )}
                  />
                </TwoColumnWrapper>
                <TwoColumnWrapper isMobile={isMobile}>
                  <Controller
                    name="investmentTeamDefault"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Select
                        label="Investment Team"
                        value={value || ''}
                        setValue={onChange}
                        options={usersSelectOptions.investmentTeamDefault}
                        errorText={error?.message}
                        size="md"
                        isRequiredField
                      />
                    )}
                  />
                  <Controller
                    name="operationsTeamDefault"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Select
                        label="Operations Team"
                        value={value || ''}
                        setValue={onChange}
                        options={usersSelectOptions.operationsTeamDefault}
                        errorText={error?.message}
                        size="md"
                        isRequiredField
                      />
                    )}
                  />
                </TwoColumnWrapper>
                <TwoColumnWrapper isMobile={isMobile}>
                  <Controller
                    name="complianceTeamDefault"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Select
                        label="Compliance Team"
                        value={value || ''}
                        setValue={onChange}
                        options={usersSelectOptions.complianceTeamDefault}
                        errorText={error?.message}
                        size="md"
                        isRequiredField
                      />
                    )}
                  />
                </TwoColumnWrapper>
              </EditInvestorFormFieldsWrapper>
            </EditDefaultUsersContentWrapper>
            <FormFooter onCancel={handleCloseModalPage} disableSaveButton={isSubmitting} showRequiredFieldsWarning={false} />
          </form>
        </EditInvestorModalPageWrapper>
      </MainWrap>
    </>
  );
};

const EditDefaultUsersContentWrapper = styled.div`
  padding: 40px;
  border-radius: 10px;
  border: 1px solid ${themeVar('border.base')};
  background: ${themeVar('layer.1')};
`;
